import { useState, useEffect } from 'react';
import { reviewsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Loader2, Lock } from 'lucide-react';
import { formatDateTime } from '../../lib/utils';

const ELIGIBLE_PROFILES = ['super_admin_profile', 'reviewer_ea_profile', 'hr_manager_profile'];

export function ManagementNotesPanel({ reviewId }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const eligible = ELIGIBLE_PROFILES.includes(user?.profile_id);

  const fetchNotes = () => {
    reviewsAPI.getNotes(reviewId).then((r) => setNotes(r.data || [])).catch((e) => {
      if (e.response?.status === 403) setNotes(false);
      else toast.error('Failed to load Management Notes');
    });
  };
  useEffect(() => { if (eligible) fetchNotes(); /* eslint-disable-next-line */ }, [reviewId, eligible]);

  if (!eligible) return null;
  if (notes === null) return null;
  if (notes === false) return null; // own review — section hidden entirely

  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await reviewsAPI.addNote(reviewId, text.trim());
      setText('');
      fetchNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t pt-4 mt-4" data-testid="management-notes-panel">
      <h4 className="text-sm font-semibold flex items-center gap-1 mb-2"><Lock className="h-3 w-3" /> Management Notes (private)</h4>
      <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
        {notes.length === 0 && <p className="text-xs text-muted-foreground">No notes yet.</p>}
        {notes.map((n) => (
          <div key={n.id} className="text-xs bg-muted/50 rounded-md p-2" data-testid={`management-note-${n.id}`}>
            <p>{n.text}</p>
            <p className="text-muted-foreground mt-1">{n.created_by_name} · {formatDateTime(n.created_at)}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a private note…"
          maxLength={2000} className="text-sm" data-testid="management-note-input" />
        <Button size="sm" onClick={addNote} disabled={saving} data-testid="management-note-add-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
        </Button>
      </div>
    </div>
  );
}
